import React from 'react';
import { Archive } from 'lucide-react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { Alert, AlertDescription, AlertTitle } from '@dashboard/components/ui/alert';
import { formatDateTime } from '@dashboard/lib/format-date';
import isDraftStatus from '@dashboard/services/status/isDraftStatus';
import isSubscriptionSuccessful from '@dashboard/services/status/isSubscriptionSuccesful';

/** Status alerts above both columns, so they lead the page on every width. Ends where the cards end on desktop. */
export const OrderAlerts = ({ children }) => (
  <div className="flex flex-col gap-y-3 px-4 pt-1 pb-3 empty:hidden max-w-5xl">{children}</div>
);

/** The charge alerts shared by payments and bookings, with the same conditions and copy as before. */
export const ChargeAlerts = ({ payment, canceledTitle, archivedTitle, showCanceledAt = false }) => (
  <OrderAlerts>
    {archivedTitle && payment.archivedAt != null && (
      <div>
        <Alert>
          <Archive />
          <AlertTitle>{archivedTitle}</AlertTitle>
          <AlertDescription>
            Hidden from your bookings list since {formatDateTime(payment.archivedAt)}.
          </AlertDescription>
        </Alert>
      </div>
    )}
    {payment.orderStatus === 'canceled' && (
      <div>
        <Alert variant="destructive">
          <ExclamationCircleIcon />
          <AlertTitle>{canceledTitle}</AlertTitle>
          {showCanceledAt && payment.canceledAt != null && (
            <AlertDescription>{formatDateTime(payment.canceledAt)}</AlertDescription>
          )}
          {payment.reason != null && <AlertDescription>{payment.reason}</AlertDescription>}
        </Alert>
      </div>
    )}
    {(payment.status === 'failed' || payment.status === 'canceled') && (
      <div>
        <Alert variant="destructive">
          <ExclamationCircleIcon />
          <AlertTitle>Payment failed</AlertTitle>
          <AlertDescription>{payment.paymentError}</AlertDescription>
        </Alert>
      </div>
    )}
    {payment.status === 'created' && (
      <div>
        <Alert variant="warning">
          <ExclamationCircleIcon />
          <AlertTitle>Payment created</AlertTitle>
          <AlertDescription>The payment has been created but not yet completed.</AlertDescription>
        </Alert>
      </div>
    )}
    {payment.status === 'pending' && (
      <div>
        <Alert variant="warning">
          <ExclamationCircleIcon />
          <AlertTitle>Payment pending</AlertTitle>
          <AlertDescription>
            Customer has completed the payment, but the payment is still pending.
          </AlertDescription>
        </Alert>
      </div>
    )}
  </OrderAlerts>
);

/** The subscription page's alerts, with the same conditions and copy as before. */
export const SubscriptionAlerts = ({ subscription }) => (
  <OrderAlerts>
    {!isSubscriptionSuccessful(subscription.status) && !isDraftStatus(subscription.status) && (
      <div>
        <Alert variant="destructive">
          <ExclamationCircleIcon />
          <AlertTitle>Subscription failed</AlertTitle>
          <AlertDescription>{subscription.paymentError}</AlertDescription>
        </Alert>
      </div>
    )}
    {subscription.status === 'canceled' && (
      <div>
        <Alert variant="destructive">
          <ExclamationCircleIcon />
          <AlertTitle>Subscription canceled</AlertTitle>
          {subscription.canceledAt != null && (
            <AlertDescription>{formatDateTime(subscription.canceledAt)}</AlertDescription>
          )}
          {subscription.reason && <AlertDescription>{subscription.reason}</AlertDescription>}
        </Alert>
      </div>
    )}
  </OrderAlerts>
);

export const SubmissionAlerts = ({ submission }) => (
  <OrderAlerts>
    {submission.orderStatus === 'canceled' && (
      <div>
        <Alert variant="destructive">
          <ExclamationCircleIcon />
          <AlertTitle>Submission canceled</AlertTitle>
          {submission.canceledAt != null && (
            <AlertDescription>{formatDateTime(submission.canceledAt)}</AlertDescription>
          )}
          {submission.reason && <AlertDescription>{submission.reason}</AlertDescription>}
        </Alert>
      </div>
    )}
  </OrderAlerts>
);
